import { useEffect, useState } from 'react'
import { Grid } from '@mui/material'
import { useDispatch } from 'react-redux'
import InvoicesGrid from 'components/generic/datagrid/InvoicesGrid'
import { useGetInvoicesV2Query } from 'services/invoices'
import { dateFormatter, roundTo } from 'utils/data-manipulation/valueFormatter'
import { clearConsumerInformation } from 'state/outboundTicket'
import { clearSalesOrderId, clearSalesOrderFilters } from 'state/salesOrder'
import {
  CURRENCY_PREPENDS,
  INVOICE_STATUSES,
  ROUNDING_PRECISIONS,
  SORTABLE_INVOICE_FIELDS,
  PAGE_SIZE,
} from 'utils/constants'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader'
import { useSelectFilter } from 'utils/custom-hooks/useSelectFilter'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'

const InvoicesContainer = () => {
  const dispatch = useDispatch()

  const { defaultCurrency } = useAppSelector(state => state.settings)
  const { currencyExchangeRatesFF, supportVoidInvoice } = useAppSelector(state => state.featureFlags?.variables || {})

  useEffect(() => {
    dispatch(clearConsumerInformation())
    dispatch(clearSalesOrderId())
    dispatch(clearSalesOrderFilters())
  }, [])

  useGlobalLoader(false)

  const { allowedOptions, renderSelectFilter } = useSelectFilter({
    localStorageKey: 'invoice-status-filter',
    options: [INVOICE_STATUSES.OPEN, INVOICE_STATUSES.PAID, INVOICE_STATUSES.VOID, INVOICE_STATUSES.CLOSED],
    defaultOptions: [INVOICE_STATUSES.OPEN.id, INVOICE_STATUSES.PAID.id],
    label: 'Status',
  })

  const [queryOptions, setQueryOptions] = useState({
    filter: {
      sort: {
        field: SORTABLE_INVOICE_FIELDS.INVOICE_TICKET_ID,
        ascending: false,
      },
      statuses: allowedOptions,
      search: '',
    },
    page: 1,
    pageSize: PAGE_SIZE,
  })

  useEffect(() => {
    setQueryOptions(prevQuery => ({
      ...prevQuery,
      filter: {
        ...prevQuery.filter,
        statuses: allowedOptions,
      },
    }))
  }, [allowedOptions])

  const { data, isFetching } = useGetInvoicesV2Query(
    {
      ...queryOptions,
      filter: JSON.stringify(queryOptions.filter),
    },
    { skip: !queryOptions.filter?.sort?.field },
  )

  const transformedRows = (data?.Items || []).slice().map(invoice => {
    const invoiceCurrency = invoice?.currency
    const invoicePricePrepend =
      !invoiceCurrency || defaultCurrency === invoiceCurrency
        ? CURRENCY_PREPENDS[defaultCurrency]?.hc
        : CURRENCY_PREPENDS[invoiceCurrency]?.fc

    return {
      id: invoice.id,
      invoiceNumber: invoice.invoiceTicketId,
      status: INVOICE_STATUSES[invoice.status]?.name || invoice.status,
      customer: invoice.customerDisplayName,
      customerId: invoice.customerId,
      tickets: invoice.outboundTicketIds?.length,
      outboundTicketTags: invoice.outboundTicketTags,
      orders: invoice.orders || [], // New unified orders array
      salesOrderNumber: invoice.salesOrderTicketId, // Legacy field for backward compatibility
      salesOrderId: invoice.salesOrderId,
      pricePrepend: currencyExchangeRatesFF ? invoicePricePrepend : '$',
      amount: roundTo(invoice.total, ROUNDING_PRECISIONS.COST).toFixed(2),
      issueDate: dateFormatter(invoice.issueDate),
      dueDate: dateFormatter(invoice.dueDate),
      materialTypeCommonNames: invoice.materialTypeCommonNames,
    }
  })

  return (
    <Grid p={3} sx={{ height: '100%' }} container flexDirection="column">
      <InvoicesGrid
        loading={isFetching}
        rows={transformedRows}
        allowedOptions={allowedOptions}
        renderSelectFilter={renderSelectFilter}
        selectFilterPath={supportVoidInvoice ? 'status' : null}
        filter={queryOptions?.filter}
        setFilter={newFilter => setQueryOptions(prevState => ({ ...prevState, filter: newFilter }))}
        useBackendSortAndFilter
        rowCount={data?.totalResultsCount || 0}
        paginationMode="server"
        onPageChange={newPageNumber => setQueryOptions(prevState => ({ ...prevState, page: newPageNumber + 1 }))}
        onPageSizeChange={newPageSize => setQueryOptions(prevState => ({ ...prevState, pageSize: newPageSize }))}
        setQueryOptions={setQueryOptions}
        autoPageSize={false}
        pageSize={queryOptions.pageSize}
      />
    </Grid>
  )
}

export default InvoicesContainer
